import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { List, X, WhatsappLogo, Phone, FacebookLogo, InstagramLogo, LinkedinLogo, EnvelopeSimple, CreditCard } from "@phosphor-icons/react";
import { NAV, CONTACT } from "@/data/site";

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleScrollNav = (e, item) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== item.to) {
      navigate(item.to);
      setTimeout(() => scrollToId(item.scrollTo), 500);
    } else {
      scrollToId(item.scrollTo);
    }
  };

  const payNow = () => {
    setOpen(false);
    navigate("/pay");
  };

  return (
    <header data-testid="site-header">
      {/* TOP BAR */}
      <div className="bg-brand-navy text-white text-xs" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-9">
          <div className="flex items-center gap-5">
            <a href={`tel:${CONTACT.primaryPhone}`} className="flex items-center gap-1.5 hover:text-yellow-400 transition-colors" data-testid="topbar-phone">
              <Phone size={14} weight="fill" className="text-brand-orange" /> {CONTACT.phones[0]}
            </a>
            <a href={`mailto:${CONTACT.emails[0]}`} className="hidden sm:flex items-center gap-1.5 hover:text-yellow-400 transition-colors">
              <EnvelopeSimple size={14} weight="fill" className="text-brand-orange" /> {CONTACT.emails[0]}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/track" className="hidden md:inline uppercase tracking-wider text-[10px] font-bold text-yellow-400 hover:text-white transition-colors" data-testid="topbar-track">Track Booking</Link>
            <span className="hidden md:inline text-white/30">|</span>
            <span className="hidden sm:inline uppercase tracking-widest text-white/60 text-[10px]">Follow Us</span>
            <a href={CONTACT.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-yellow-400 transition-colors"><FacebookLogo size={16} weight="fill" /></a>
            <a href={CONTACT.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-yellow-400 transition-colors"><InstagramLogo size={16} weight="fill" /></a>
            <a href={CONTACT.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-yellow-400 transition-colors"><LinkedinLogo size={16} weight="fill" /></a>
          </div>
        </div>
      </div>

      {/* MAIN NAV */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-[0_4px_20px_-4px_rgba(46,42,128,0.10)]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <img src="/assets/cluetrip-logo.png" alt="Cluetrip Holidays" className="h-11 md:h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) =>
              item.scrollTo ? (
                <a
                  key={item.label}
                  href={`/#${item.scrollTo}`}
                  onClick={(e) => handleScrollNav(e, item)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="font-body text-sm font-bold uppercase tracking-wide text-brand-navy transition-colors hover:text-brand-orange cursor-pointer"
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-body text-sm font-bold uppercase tracking-wide transition-colors hover:text-brand-orange ${
                      isActive ? "text-brand-orange" : "text-brand-navy"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp"
              className="hidden sm:inline-flex text-brand-navy hover:text-brand-teal transition-colors"
              aria-label="WhatsApp"
            >
              <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
            </a>
            <button
              onClick={payNow}
              data-testid="header-pay-now"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-yellow-400 px-6 py-2.5 text-sm font-extrabold uppercase tracking-wide text-brand-navy transition-transform hover:-translate-y-0.5"
            >
              <CreditCard size={17} weight="fill" /> Pay Now
            </button>
            <button
              className="lg:hidden text-brand-navy"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <List size={26} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-brand-sand px-5 py-5 flex flex-col gap-4" data-testid="mobile-menu">
            {NAV.map((item) =>
              item.scrollTo ? (
                <a key={item.label} href={`/#${item.scrollTo}`} onClick={(e) => handleScrollNav(e, item)} className="font-body text-base font-bold uppercase text-brand-navy">
                  {item.label}
                </a>
              ) : (
                <NavLink key={item.label} to={item.to} end={item.to === "/"} onClick={() => setOpen(false)} className="font-body text-base font-bold uppercase text-brand-navy">
                  {item.label}
                </NavLink>
              )
            )}
            <button onClick={payNow} className="mt-2 rounded-full bg-yellow-400 px-6 py-3 text-sm font-extrabold uppercase text-brand-navy">
              Pay Now
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
